/**
 * coerceModeUrl — shared helper for /brawl, /pve, /abilitytest, /lobby
 * URL coercion. Each mode URL re-writes its own search params on first
 * mount so the rest of the app sees `?map=X&preset=Y&skipLogin=1...`
 * without DJ having to type them. Adding a 5th mode (e.g. /duel, /raid)
 * is now a one-line `coerceModeUrl(...)` call instead of another 25-line
 * branch in main.tsx.
 *
 * Usage in main.tsx:
 *   coerceModeUrl({
 *     path: '/brawl',
 *     mapId: 'brawl-arena',
 *     resetMarkerKey: 'helix_brawl_reset_v1',
 *     config: BRAWL_CONFIG,
 *   });
 *
 * Class is NOT injected by default — the character roster owns the choice.
 * The exception is `autoClass` (opt-in per mode): a mode whose class choice
 * is irrelevant (the /editor, where you just want into the tools) can set
 * `autoClass: 'mage'` to auto-pick and skip the picker. Game modes leave it
 * unset so players still choose. Direct deep-links (`/brawl?class=mage`)
 * always work because the picker honors the param.
 */
import { bootPathname, bootSearch, bootSearchParams } from './bootUrl';

export interface ModeUrlConfig {
  /** Wipe per-tab cache on first navigation. */
  readonly resetOnLoad: boolean;
  /** Preset to inject into ?preset=X (devConfig resolver reads this). */
  readonly preset: string;
  /** ?skipSplash=1 to skip splash screen. */
  readonly skipSplash: boolean;
  /** ?skipLogin=1 to skip login screen. */
  readonly skipLogin: boolean;
  /** Inject ?skipLogin=0 so the Twitch-only AuthScreen gates entry — the
   *  DevConfig default is skip-on-every-tier, so a mode that WANTS the login
   *  must force the param, not merely omit skipLogin. Two escape hatches:
   *  strict localhost (DJ's dev seat keeps the zero-click flow) and an
   *  explicit ?skipLogin=… already in the URL (the race runtime probes pass
   *  skipLogin=1). Used by /gmrace — race identity is Twitch identity. */
  readonly forceLogin?: boolean;
  /** ?devPanelsOn=1 — optional, opens debug panels on boot (e.g. /abilitytest). */
  readonly devPanelsOn?: boolean;
  /** ?testhooks=1 — optional, installs the render-truth test hooks
   *  (__h2kRenderState / __h2kCast). Modes whose PRIMARY surface reads them
   *  (the Ability Grounds live readout) set this; Playwright URLs pass the
   *  param directly. */
  readonly testhooks?: boolean;
  /** ?abilityCapture=1 — optional, auto-screenshots every successful local
   *  cast to the server's /api/diag/ability-captures store (60-min
   *  retention). The Ability Grounds sets this — it's the QA surface the
   *  captures exist for. */
  readonly abilityCapture?: boolean;
  /** ?qaScenario=<id> — optional local QA preparation request. NetworkManager
   * sends the value once after joining; the server independently requires
   * memory-only mode + GM permission before applying it. */
  readonly qaScenario?: string;
  /** Window-engine urlSeed params to set to '1' — opens the named registry
   *  windows on boot (WindowManagerContext.seedFromUrl). e.g. the Grounds
   *  seeds ['grounds'] so its control window auto-opens on the route. */
  readonly seedWindows?: readonly string[];
  /** ?editorOn=1 — optional, opens the level-editor overlay on boot (/editor).
   *  Flips DevConfig.editorOverlayOnByDefault so the EditorOverlay mounts
   *  without a Backquote toggle. Only meaningful alongside `preset: 'editor'`
   *  (which sets DevConfig.systems.editor = true). */
  readonly editorOn?: boolean;
  /** ?classSwitcher=1 — optional, shows the pinned top-of-screen quick class
   *  switcher (ClassSwitcherTopBar). DEFAULT OFF for every mode (DJ 2026-07-10
   *  — it's a QA/dev respawn convenience, not player chrome); only dev
   *  surfaces (/debug, /abilitygrounds) opt in. An explicit ?classSwitcher=…
   *  in the URL always wins (QA harnesses can force it on any mode). */
  readonly classSwitcher?: boolean;
  /** ?class=X — optional auto-pick that skips the roster. Only for
   *  modes where the class is irrelevant (e.g. /editor → 'mage'); leave unset
   *  for game modes so players choose. The special value `'random'` rolls a
   *  playable class per visit (e.g. /gmrace — sprite variety in the pack;
   *  the racer loadout overlay makes the class ability-irrelevant). An
   *  explicit ?class=… already in the URL always wins (deep links, probes). */
  readonly autoClass?: string;
  /** With autoClass: route the auto-pick through the PreGameFlow FUNNEL
   *  (its ?class auto-create effect — zero cards shown) instead of the
   *  synthetic `pickCharacter=0` dev spawn. REQUIRED for modes real (Twitch)
   *  accounts reach: the synthetic dev token 410s on staging/prod, so
   *  pickCharacter=0 would dead-end an authed public visitor. */
  readonly autoClassPicker?: boolean;
  /** ?createCharacter=1 — optional. Routes the funnel straight to the full
   *  CharacterCustomizer (name + class + appearance) instead of the quick
   *  picker, then enters the game on this mode's map. Used by /intro so "make
   *  a hero and start the story" is one card. */
  readonly create?: boolean;
  /** Force the full character roster (CharacterSelectScreen with resume +
   *  abandon) instead of the quick picker: injects ?quickPicker=0 +
   *  ?pickCharacter=1. Used by /resume so you can pick a saved hero to
   *  continue (or abandon). */
  readonly fullRoster?: boolean;
  /** ?touchControls=1 — forces touch controls overlay (virtual joysticks & action pad). */
  readonly touchControls?: boolean;
  /** ?noclip=1 — collision bypass. */
  readonly noclip?: boolean;
}

/** The `autoClass: 'random'` pool — the 9 playable classes (no dev). */
const RANDOM_CLASS_POOL = [
  'warrior', 'mage', 'hunter', 'paladin', 'rogue',
  'shaman', 'priest', 'warlock', 'druid',
] as const;

export interface CoerceModeUrlOptions {
  /** Pathname this coercion applies to (e.g. '/brawl'). */
  path: string;
  /** Map id to inject into ?map=X (e.g. 'brawl-arena'). */
  mapId: string;
  /** sessionStorage key guarding the per-tab reset wipe (e.g. 'helix_brawl_reset_v1'). */
  resetMarkerKey: string;
  /** Config object (BRAWL_CONFIG, PVE_CONFIG, etc.) — read for boolean toggles. */
  config: ModeUrlConfig;
}

/**
 * Re-writes the URL params for the given mode on first mount.
 * Returns true if a path matched + coercion applied (caller doesn't need
 * the return value today; left for future telemetry).
 *
 * Safe in non-browser contexts (returns false; no throws).
 */
export function coerceModeUrl(opts: CoerceModeUrlOptions): boolean {
  if (typeof window === 'undefined') return false;
  if (bootPathname() !== opts.path) return false;

  const sp = bootSearchParams();

  if (opts.config.resetOnLoad && typeof sessionStorage !== 'undefined') {
    if (sessionStorage.getItem(opts.resetMarkerKey) !== '1') {
      sessionStorage.setItem(opts.resetMarkerKey, '1');
      sp.set('reset', '1');
    }
  }

  sp.set('map', opts.mapId);
  sp.set('preset', opts.config.preset);
  if (opts.config.skipSplash) sp.set('skipSplash', '1');
  if (opts.config.skipLogin) sp.set('skipLogin', '1');
  // forceLogin: gate the mode behind the Twitch AuthScreen on public hosts.
  // Strict-localhost mirrors useAuth's isStrictLocalhost — the dev seat keeps
  // its zero-click spawn; an explicit ?skipLogin=… (probe harnesses) wins.
  if (opts.config.forceLogin && !sp.has('skipLogin')) {
    const host = window.location.hostname;
    const isStrictLocalhost = host === 'localhost' || host === '127.0.0.1';
    if (!isStrictLocalhost) sp.set('skipLogin', '0');
  }
  if (opts.config.devPanelsOn) sp.set('devPanelsOn', '1');
  if (opts.config.testhooks) sp.set('testhooks', '1');
  if (opts.config.qaScenario) sp.set('qaScenario', opts.config.qaScenario);
  // Respect an explicit ?abilityCapture=0 — the ability-audit harness drives
  // hundreds of casts and must opt OUT of per-cast frame uploads.
  if (opts.config.abilityCapture && !sp.has('abilityCapture')) sp.set('abilityCapture', '1');
  for (const seed of opts.config.seedWindows ?? []) sp.set(seed, '1');
  if (opts.config.classSwitcher && !sp.has('classSwitcher')) sp.set('classSwitcher', '1');
  if (opts.config.editorOn) sp.set('editorOn', '1');
  if (opts.config.touchControls && !sp.has('touchControls')) sp.set('touchControls', '1');
  if (opts.config.noclip && !sp.has('noclip')) sp.set('noclip', '1');
  // Opt-in auto-pick (e.g. /editor → mage) so the class picker is skipped.
  // 'random' rolls a playable class per visit; an explicit ?class=… in the
  // URL wins (deep links + probe harnesses stay deterministic).
  if (opts.config.autoClass) {
    if (!sp.has('class')) {
      sp.set('class', opts.config.autoClass === 'random'
        ? RANDOM_CLASS_POOL[Math.floor(Math.random() * RANDOM_CLASS_POOL.length)]
        : opts.config.autoClass);
    }
    // autoClassPicker keeps the funnel — PreGameFlow auto-creates
    // from ?class with no cards, which works for real (Twitch) accounts.
    // The synthetic pickCharacter=0 spawn is localhost/dev-only (410 on prod).
    if (!opts.config.autoClassPicker) sp.set('pickCharacter', '0');
  }
  // Opt-in: route to the full character creator (e.g. /intro). pickCharacter=1
  // forces the funnel (disables the dev-session synthetic auto-spawn) so the
  // creator actually shows; createCharacter=1 then routes funnel→creator.
  if (opts.config.create) {
    sp.set('createCharacter', '1');
    sp.set('pickCharacter', '1');
  }
  // Opt-in: show the full character roster (resume/abandon) instead of the
  // quick picker (e.g. /resume). pickCharacter=1 forces the funnel.
  if (opts.config.fullRoster) {
    sp.set('quickPicker', '0');
    sp.set('pickCharacter', '1');
  }

  const newSearch = sp.toString();
  const targetHref = `${opts.path}${newSearch ? '?' + newSearch : ''}`;
  if (bootPathname() + bootSearch() !== targetHref) {
    window.history.replaceState({}, '', targetHref);
  }
  return true;
}
